import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import type { CardLoginEventDto } from '@gc/shared';

import { filterLogins, LOGIN_FILTERS, type LoginFilter } from './cards';

interface CardActivityTableProps {
  /** Null until the first load resolves. */
  events: CardLoginEventDto[] | null;
  loading: boolean;
  error: string | null;
  filter: LoginFilter;
  onFilterChange: (filter: LoginFilter) => void;
  onRefresh: () => void;
}

/**
 * Venue-wide card taps from GET /customers/card-logins, matched and unmatched.
 *
 * The unmatched cut is the point of the feed: the server records failed taps
 * deliberately, because a run of them is the earliest signal that a reader or
 * a card is dying — or that a card was simply never assigned. Presentational
 * (state arrives as props) so the error-before-empty rule is testable in
 * static markup.
 */
export function CardActivityTable({
  events,
  loading,
  error,
  filter,
  onFilterChange,
  onRefresh,
}: CardActivityTableProps) {
  const { t, i18n } = useTranslation();

  const rows = filterLogins(events ?? [], filter);

  // Seconds matter here: a dying reader shows up as a burst of taps inside
  // one minute, which short time would collapse into identical rows.
  const dateTime = (iso: string) => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'medium',
      numberingSystem: 'latn',
    }).format(parsed);
  };

  return (
    <section aria-label={t('cards.activity.title')}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="me-2 text-base font-semibold text-content">{t('cards.activity.title')}</h2>
        {LOGIN_FILTERS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => onFilterChange(candidate)}
            data-filter={candidate}
            aria-pressed={candidate === filter}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              candidate === filter
                ? 'border-primary bg-secondary font-medium text-primary'
                : 'border-subtle text-content-secondary hover:bg-hover'
            }`}
          >
            {t(`cards.activity.filters.${candidate}`)}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={onRefresh}
          className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          {t('cards.activity.refresh')}
        </button>
      </div>

      {filter === 'unmatched' ? (
        <p className="mb-3 text-xs text-content-muted">{t('cards.activity.unmatchedHint')}</p>
      ) : null}

      {error ? (
        // Error before empty, always: "no taps yet" while the API is down
        // would hide exactly the reader failure this screen exists to show.
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : loading && events === null ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-content-muted">
          {filter === 'unmatched' ? t('cards.activity.emptyUnmatched') : t('cards.activity.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-card border border-subtle">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
              <tr>
                <Th>{t('cards.activity.columns.when')}</Th>
                <Th>{t('cards.activity.columns.customer')}</Th>
                <Th>{t('cards.activity.columns.device')}</Th>
                <Th>{t('cards.activity.columns.cardId')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((event) => (
                <tr key={event.id} className="border-t border-subtle">
                  <Td>
                    <span className="tabular">{dateTime(event.createdAt)}</span>
                  </Td>
                  <Td>
                    {event.succeeded && event.customerName ? (
                      <span className="font-medium text-content">{event.customerName}</span>
                    ) : (
                      <span className="rounded-full border border-subtle px-2 py-0.5 text-xs font-medium text-danger">
                        {t('cards.activity.unrecognised')}
                      </span>
                    )}
                  </Td>
                  <Td>{event.deviceName ?? '—'}</Td>
                  <Td>
                    <span className="tabular">{event.cardId}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="whitespace-nowrap px-3 py-2 text-start font-medium">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 text-start text-content-secondary">{children}</td>
  );
}
